<template>
  <nav
    class="nav"
    aria-label="Primary"
    :aria-hidden="activeModal ? 'true' : undefined"
    :inert="Boolean(activeModal)"
  >
    <div class="wrap nav-inner">
      <a class="brand brand-lockup" href="#" aria-label="Zafda home">
        <img class="brand-mark" src="/assets/zafda-logo.png" alt="Zafda logo">
        <span>ZAFDA</span>
      </a>
      <div class="nav-links">
        <button class="login login-open" type="button" @click="openModal('login', $event)">
          Login
        </button>
      </div>
    </div>
  </nav>

  <main :aria-hidden="activeModal ? 'true' : undefined" :inert="Boolean(activeModal)">
    <header class="hero">
      <div class="wrap hero-copy">
        <div class="kicker">Personalized science stories for K–12</div>
        <h1>Science shouldn't feel distant.</h1>
        <p>Zafda turns familiar moments into stories that help you see science not as another subject to memorize, but as a way of understanding the world you already enjoy.</p>
        <div class="buttons">
          <a class="btn primary" href="#how">See how Zafda works</a>
          <a class="btn secondary" href="#pricing">$7 / month</a>
        </div>
      </div>
    </header>

    <section id="how">
      <div class="wrap">
        <div class="section-kicker">How Zafda works</div>
        <h2>Every science concept has a story. We make it one you will want to hear.</h2>
        <p class="lede">Choose what you are learning. Add a sport you already love. Zafda turns both into a personalized story that makes the physics easier to see and remember.</p>
        <div class="formula">
          <div class="formula-card">
            <small>What you are learning</small>
            <strong>Gravity</strong>
          </div>
          <div class="symbol" aria-hidden="true">+</div>
          <div class="formula-card">
            <small>What you already love</small>
            <strong>Basketball</strong>
          </div>
          <div class="symbol" aria-hidden="true">→</div>
          <div class="formula-card result">
            <small>Your story</small>
            <strong>A Game on Mars</strong>
          </div>
        </div>
      </div>
    </section>

    <section id="stories">
      <div class="wrap">
        <div class="section-kicker">Physics through stories</div>
        <h2>Start with a moment you already know. Discover the physics inside it.</h2>
        <p class="lede">Every story begins with something specific enough to picture, then reveals the forces, motion and energy shaping what happens next.</p>
        <div class="story-grid">
          <article class="story-card">
            <div>
              <div class="sport">Soccer</div>
              <h3>Messi's Longest Pass</h3>
            </div>
            <p>Follow the pass to explore velocity, relative speed, distance and how motion changes across the field.</p>
          </article>
          <article class="story-card">
            <div>
              <div class="sport">Basketball</div>
              <h3>Curry From Downtown</h3>
            </div>
            <p>Follow the shot to explore launch angle, acceleration, energy transfer and the changing speed of the ball.</p>
          </article>
          <article class="story-card">
            <div>
              <div class="sport">Baseball</div>
              <h3>The Perfect Curveball</h3>
            </div>
            <p>Follow the pitch to explore air pressure, spin, drag and the forces that bend its path.</p>
          </article>
          <article class="story-card">
            <div>
              <div class="sport">Football</div>
              <h3>The Goal-Line Tackle</h3>
            </div>
            <p>Follow the collision to explore momentum, force, mass and how motion changes in an instant.</p>
          </article>
        </div>
      </div>
    </section>

    <section id="physics">
      <div class="wrap physics-grid">
        <div class="physics-copy">
          <div class="section-kicker">Physics you can see</div>
          <h2>Once you see the forces, the story changes.</h2>
          <p class="lede">A familiar sports moment can reveal the physics hiding in plain sight. You can see the launch angle, gravity, velocity, distance, height and trajectory instead of only reading definitions.</p>
        </div>
        <div class="physics-image">
          <img
            src="/assets/physics-basketball-1280.webp"
            srcset="/assets/physics-basketball-800.webp 800w, /assets/physics-basketball-1280.webp 1280w, /assets/physics-basketball-1672.webp 1672w"
            sizes="(max-width: 900px) 100vw, 55vw"
            loading="lazy"
            decoding="async"
            alt="Basketball arena with physics overlays showing gravity, velocity, launch angle, height and distance"
          >
          <div class="physics-caption">One basketball shot. Multiple physics ideas you can see at once.</div>
        </div>
      </div>
    </section>

    <section id="parents">
      <div class="wrap">
        <div class="section-kicker">For parents</div>
        <div class="parents-heading-row parents-heading-single">
          <h2>Built for kids. Designed with parents.</h2>
        </div>
        <div class="parents-grid">
          <div class="parent-card">
            <strong>Connected to school</strong>
            <p>You explore physics concepts that connect naturally with what you are learning in class.</p>
          </div>
          <div class="parent-card">
            <strong>Built around interests</strong>
            <p>You learn through sports and situations that already feel familiar, relevant and worth exploring.</p>
          </div>
          <div class="parent-card">
            <strong>Focused on understanding</strong>
            <p>You see why something happens before worrying about definitions, formulas or facts to memorize.</p>
          </div>
          <div class="parent-card">
            <strong>Made for discussion</strong>
            <p>You can question ideas, discuss concepts and keep exploring beyond the first explanation or story.</p>
          </div>
        </div>
      </div>
    </section>

    <section id="pricing">
      <div class="wrap">
        <div class="section-kicker">Simple pricing</div>
        <h2>One subscription. All of Zafda.</h2>
        <div class="price-wrap">
          <div class="price-card">
            <div class="price-label">Individual subscription</div>
            <div class="price">$7 <small>/ month</small></div>
            <p>Explore personalized physics stories built around the sports and moments you already love.</p>
            <ul>
              <li>Personalized physics stories</li>
              <li>K–12 learning levels</li>
              <li>Multiple sports and interests</li>
              <li>Concept discussions and exploration</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </main>

  <div
    id="contactModal"
    class="info-modal"
    :class="{ open: activeModal === 'contact' }"
    :aria-hidden="activeModal !== 'contact'"
  >
    <div class="info-backdrop" aria-hidden="true" data-close-info @click="closeModal"></div>
    <div
      ref="contactDialog"
      class="info-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contactTitle"
    >
      <button class="info-close" type="button" aria-label="Close contact" data-close-info @click="closeModal">×</button>
      <div class="section-kicker">Write to us</div>
      <h3 id="contactTitle">Send us a message.</h3>
      <p>Have a question, feedback or want to learn more about Zafda? Send us a note.</p>
      <form
        id="contactForm"
        class="contact-form"
        :class="{ hidden: contactSucceeded }"
        novalidate
        @submit.prevent="submitContact"
      >
        <div>
          <label for="contactName">Name</label>
          <input
            id="contactName"
            ref="contactNameInput"
            v-model="contactName"
            name="name"
            type="text"
            autocomplete="name"
            placeholder="Your name"
            required
          >
        </div>
        <div>
          <label for="contactEmail">Email</label>
          <input
            id="contactEmail"
            v-model="contactEmail"
            name="email"
            type="email"
            autocomplete="email"
            placeholder="you@example.com"
            required
            aria-describedby="contactEmailError"
            :aria-invalid="contactEmailInvalid ? 'true' : undefined"
            :class="{ invalid: contactEmailInvalid }"
          >
          <div id="contactEmailError" class="contact-email-error" aria-live="polite">
            {{ contactEmailInvalid ? 'Enter a valid email address.' : '' }}
          </div>
        </div>
        <div>
          <label for="contactMessage">Message</label>
          <textarea
            id="contactMessage"
            v-model="contactMessage"
            name="message"
            autocomplete="off"
            placeholder="Write your message"
            required
          ></textarea>
        </div>
        <button id="contactSend" type="submit" class="btn primary" :disabled="!contactEmailValid">
          Send message
        </button>
        <div id="contactSuccess" class="contact-success error" aria-live="polite" role="status">
          {{ contactStatus }}
        </div>
      </form>
      <div
        id="contactSuccessWrap"
        ref="contactSuccess"
        class="contact-success-wrap"
        :class="{ show: contactSucceeded }"
        tabindex="-1"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <div class="contact-success-icon" aria-hidden="true">✓</div>
        <strong>Thank you.</strong>
        <p>We will get back to you at the earliest.</p>
      </div>
    </div>
  </div>

  <div
    id="privacyModal"
    class="info-modal"
    :class="{ open: activeModal === 'privacy' }"
    :aria-hidden="activeModal !== 'privacy'"
  >
    <div class="info-backdrop" aria-hidden="true" data-close-info @click="closeModal"></div>
    <div
      ref="privacyDialog"
      class="info-dialog legal-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacyTitle"
    >
      <button ref="privacyClose" class="info-close" type="button" aria-label="Close privacy policy" data-close-info @click="closeModal">×</button>
      <div class="section-kicker">Legal</div>
      <h3 id="privacyTitle">Privacy Policy</h3>
      <p><strong>Last updated: December 15, 2024</strong></p>
      <h4>1. Introduction</h4>
      <p>This Privacy Policy explains how Zafda collects, uses, stores, protects and shares information when students, parents, guardians, educators, institutions and other visitors use our websites, learning experiences, communications and related services. We aim to collect only information reasonably necessary to operate Zafda, personalize learning, improve the service and protect our users.</p>
      <p>By accessing or using Zafda, you acknowledge that you have read this Privacy Policy. Where applicable law requires consent, we will seek appropriate consent before collecting or using personal information for the relevant purpose.</p>
      <h4>2. Information we may collect</h4>
      <p>We may collect information you provide directly, including names, usernames, email addresses, parent or guardian details, school or institution information, grade level, preferred sports, learning preferences, feedback, support messages and other information submitted through Zafda.</p>
      <p>We may also collect technical information automatically, such as browser type, device type, operating system, approximate location derived from network information, pages viewed, session duration, feature usage, error logs, security events and interactions with learning content.</p>
      <h4>3. Children's privacy</h4>
      <p>Zafda is designed for K–12 learners. Protecting children's privacy is especially important to us. Where required by law, we expect a parent, guardian, school or other authorized adult to provide or facilitate appropriate consent before a child provides personal information.</p>
      <p>We do not intentionally seek unnecessary personal information from children. Parents, guardians and institutions may contact us if they believe information has been collected inappropriately or should be corrected or removed.</p>
      <h4>4. How we use information</h4>
      <p>We may use information to operate Zafda, personalize stories and explanations, adapt learning content to a learner's level and interests, maintain account access, answer questions, provide customer support, improve product quality, perform analytics, prevent abuse, maintain platform security, communicate service updates and comply with legal obligations.</p>
      <p>We may use aggregated or de-identified information to understand usage patterns, improve content quality, evaluate product performance and conduct internal research.</p>
      <h4>5. Personalization and learning preferences</h4>
      <p>Zafda may use learning preferences, grade level, selected concepts, sports interests and other user-provided context to personalize science stories. Personalization is intended to improve relevance and comprehension and is not used to make high-impact decisions about education, admissions, employment, credit or other legally sensitive matters.</p>
      <h4>6. Cookies and similar technologies</h4>
      <p>Our production services may use cookies, local storage and similar technologies to maintain sessions, remember preferences, measure website performance, protect against abuse and understand how users interact with Zafda. Where required, users will be given appropriate choices relating to non-essential cookies.</p>
      <h4>7. Data sharing</h4>
      <p>We do not sell personal information. We may share limited information with service providers that help us host infrastructure, send communications, process payments, monitor security, provide analytics, deliver customer support or otherwise operate Zafda.</p>
      <p>Service providers are expected to process information only for agreed purposes and subject to appropriate contractual, confidentiality and security obligations.</p>
      <h4>8. Schools and institutions</h4>
      <p>Where Zafda is used through a school or educational institution, the institution may have its own obligations and rights relating to student information. Certain information may be controlled by the institution, and requests relating to that information may need to be directed through the institution.</p>
      <h4>9. Data protection and security</h4>
      <p>We use administrative, technical and organizational safeguards designed to reduce the risk of unauthorized access, alteration, disclosure, loss or misuse of personal information. These may include encryption in transit, access controls, authentication safeguards, monitoring, logging, backup procedures and restricted access to production systems.</p>
      <p>No online service can guarantee absolute security. Users should maintain strong passwords, avoid reusing passwords across services and notify us promptly if they believe an account has been compromised.</p>
      <h4>10. Passwords and account security</h4>
      <p>Users are responsible for keeping account credentials confidential. Passwords should not be shared with friends, classmates or unauthorized third parties. Parents or guardians may assist children with account access where appropriate.</p>
      <p>If we detect unusual activity, we may temporarily restrict access, require additional verification, reset credentials or take other reasonable measures to protect the account and service.</p>
      <h4>11. Data retention</h4>
      <p>We retain information for as long as reasonably necessary to provide the service, comply with legal obligations, resolve disputes, enforce agreements, maintain security and support legitimate business purposes. Retention periods may vary depending on the type of information and how it is used.</p>
      <h4>12. International data transfers</h4>
      <p>Zafda may use service providers or infrastructure located in different countries. Where personal information is transferred across borders, we aim to use appropriate safeguards consistent with applicable data protection requirements.</p>
      <h4>13. User and parent rights</h4>
      <p>Depending on applicable law, users, parents or guardians may have rights to request access, correction, deletion, restriction or portability of personal information, or to object to certain processing. Requests may be subject to identity verification and legal limitations.</p>
      <p>For children's accounts, we may require confirmation that the requester is an authorized parent, guardian, educator or institution representative before providing account information or making changes.</p>
      <h4>14. Marketing communications</h4>
      <p>Where permitted, we may send product updates, educational content or service announcements. Users can opt out of non-essential marketing communications using the unsubscribe mechanism provided in the message or by contacting us.</p>
      <h4>15. Security incident response</h4>
      <p>If we become aware of a security incident affecting personal information, we will investigate, take reasonable steps to contain and remediate the incident, and provide notifications where required by applicable law.</p>
      <h4>16. Complaints and dispute resolution</h4>
      <p>If you have a privacy concern, we encourage you to contact us first so we can investigate and attempt to resolve the matter. We may ask for additional information necessary to understand the concern and verify the identity or authority of the requester.</p>
      <p>Where applicable law provides a right to complain to a data protection authority or regulator, you may also exercise that right after or instead of contacting us directly.</p>
      <h4>17. Changes to this Privacy Policy</h4>
      <p>We may update this Privacy Policy as Zafda evolves, legal requirements change or our data practices change. Material changes may be communicated through the service, by email or through another appropriate channel.</p>
      <h4>18. Contact</h4>
      <p>Questions, requests or concerns relating to privacy can be submitted through the Contact form on this website. Please provide enough detail for us to understand and respond to your request.</p>
    </div>
  </div>

  <div
    id="termsModal"
    class="info-modal"
    :class="{ open: activeModal === 'terms' }"
    :aria-hidden="activeModal !== 'terms'"
  >
    <div class="info-backdrop" aria-hidden="true" data-close-info @click="closeModal"></div>
    <div
      ref="termsDialog"
      class="info-dialog legal-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="termsTitle"
    >
      <button ref="termsClose" class="info-close" type="button" aria-label="Close terms" data-close-info @click="closeModal">×</button>
      <div class="section-kicker">Legal</div>
      <h3 id="termsTitle">Terms of Use</h3>
      <p><strong>Last updated: December 15, 2024</strong></p>
      <h4>1. Agreement to these terms</h4>
      <p>These Terms of Use govern access to and use of Zafda's websites, science stories, learning features, communications and related services. By accessing or using Zafda, you agree to comply with these terms and any additional terms presented for specific features.</p>
      <h4>2. Educational purpose</h4>
      <p>Zafda is designed to supplement learning by turning science concepts into personalized stories and discussions. Zafda does not replace classroom instruction, teachers, school curriculum requirements, professional educational judgment or formal assessment.</p>
      <p>We aim to provide accurate and age-appropriate educational content, but users should use reasonable judgment and, where appropriate, verify information through teachers, textbooks and other reliable educational resources.</p>
      <h4>3. Eligibility and children's use</h4>
      <p>Children may use Zafda only with any consent, supervision or authorization required by applicable law, their parent or guardian, or their school or institution.</p>
      <h4>4. Accounts</h4>
      <p>Some Zafda services may require an account. Users must provide accurate information and maintain the confidentiality of account credentials. You are responsible for activity occurring through your account unless prohibited by applicable law.</p>
      <h4>5. Password sharing</h4>
      <p>Individual subscriptions are intended for the person or household authorized under the applicable plan. Credentials may not be shared publicly, sold, redistributed, published online or provided to unrelated third parties.</p>
      <p>We may use reasonable technical measures to detect unusual account sharing or abuse. If we believe an account is being misused, we may request verification, temporarily limit access, require a password reset or suspend access.</p>
      <h4>6. Fair usage policy</h4>
      <p>Zafda is designed for normal educational use. Users must not use automated tools, scripts, bots or other means to generate excessive requests, scrape content, overload infrastructure, bypass technical restrictions or interfere with the experience of other users.</p>
      <p>We may apply reasonable rate limits, usage thresholds or temporary restrictions where necessary to protect reliability, security and fair access. These controls are intended to prevent abuse rather than limit normal student learning.</p>
      <h4>7. Acceptable use</h4>
      <p>You may not use Zafda to violate the law, harass others, upload malicious software, attempt unauthorized access, impersonate another person, interfere with platform security, reverse engineer protected systems, scrape content at scale or use the service in a way that could harm Zafda or other users.</p>
      <h4>8. Subscriptions and billing</h4>
      <p>Paid subscriptions are billed according to the price and billing period shown at purchase. Applicable taxes may be added where required. Renewal, cancellation and refund information will be presented during the subscription process.</p>
      <h4>9. Cancellations and refunds</h4>
      <p>Users may cancel subscriptions according to the cancellation process available through the relevant account or payment method. Cancellation generally prevents future renewal but does not necessarily result in a refund for an already-paid period unless required by law or expressly stated otherwise.</p>
      <h4>10. Intellectual property</h4>
      <p>Zafda's website, branding, visual design, software, story formats, educational content, graphics and other materials are owned by or licensed to Zafda and may be protected by copyright, trademark and other intellectual property laws.</p>
      <h4>11. User content and feedback</h4>
      <p>If users submit questions, comments, feedback or other content, they retain any rights they may have in that material. Users grant Zafda the permissions reasonably necessary to host, process, display and use that material to provide and improve the service.</p>
      <h4>12. Content moderation</h4>
      <p>Where Zafda offers discussion or community features, we may review, remove or restrict content that violates these terms, creates safety concerns or materially disrupts the learning environment.</p>
      <h4>13. Service availability</h4>
      <p>We aim to operate Zafda reliably, but the service may occasionally be unavailable because of maintenance, infrastructure issues, software updates, security incidents or events outside our control.</p>
      <h4>14. Security</h4>
      <p>Users must not attempt to defeat authentication, gain unauthorized access, probe vulnerabilities, interfere with security controls or introduce harmful code. We reserve the right to take reasonable steps to protect users, accounts and infrastructure.</p>
      <h4>15. Data protection and privacy</h4>
      <p>Our handling of personal information is described in the Zafda Privacy Policy. By using the service, you acknowledge that personal information may be processed as described there and in accordance with applicable law.</p>
      <h4>16. Third-party services</h4>
      <p>Zafda may rely on third-party providers for hosting, analytics, payments, communications or other infrastructure. Those providers may have their own terms and privacy practices when users interact directly with their services.</p>
      <h4>17. Suspension and termination</h4>
      <p>We may suspend or terminate access where we reasonably believe an account is being used fraudulently, unlawfully, insecurely, in violation of these terms or in a way that materially harms Zafda or other users.</p>
      <h4>18. Disclaimers</h4>
      <p>Zafda is provided for educational purposes. To the maximum extent permitted by law, we do not guarantee uninterrupted access, perfect accuracy, particular academic outcomes or that every explanation will be suitable for every learner.</p>
      <h4>19. Limitation of liability</h4>
      <p>To the maximum extent permitted by applicable law, Zafda will not be liable for indirect, incidental, special, consequential or punitive damages arising from use of or inability to use the service. Any limitation will apply only to the extent permitted by law.</p>
      <h4>20. Indemnification</h4>
      <p>Where legally permitted, users agree to be responsible for claims, losses or costs resulting from their unlawful misuse of Zafda or material breach of these terms.</p>
      <h4>21. Dispute resolution</h4>
      <p>If a dispute arises, we encourage users to contact us first and provide a clear description of the issue. We will make a reasonable effort to review the concern and seek an informal resolution.</p>
      <p>If informal resolution is unsuccessful, disputes may be handled through the courts, arbitration, mediation or another process permitted by applicable law and specified in any more detailed terms presented to the user at purchase or account creation.</p>
      <h4>22. Complaints and escalation</h4>
      <p>Users may submit complaints through the Contact form. We may request supporting information, account details or other documentation necessary to review the issue.</p>
      <h4>23. Changes to these terms</h4>
      <p>We may update these Terms of Use as Zafda evolves. Material changes may be communicated through the website, account interface, email or another appropriate method.</p>
      <h4>24. General provisions</h4>
      <p>If any provision of these terms is found unenforceable, the remaining provisions will continue in effect. Failure to enforce a provision does not waive the right to enforce it later.</p>
      <h4>25. Contact</h4>
      <p>Questions about these terms, billing, access, account security or disputes can be submitted through the Contact form on this website.</p>
    </div>
  </div>

  <div
    id="loginModal"
    class="login-modal"
    :class="{ open: activeModal === 'login' }"
    :aria-hidden="activeModal !== 'login'"
  >
    <div class="login-backdrop" aria-hidden="true" data-close-login @click="closeModal"></div>
    <div
      ref="loginDialog"
      class="login-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="loginTitle"
    >
      <button class="login-close" type="button" aria-label="Close login" data-close-login @click="closeModal">×</button>
      <div class="section-kicker">Subscriber access</div>
      <h3 id="loginTitle">Login to Zafda</h3>
      <form novalidate @submit.prevent="submitLogin">
        <label for="loginUser">Username</label>
        <input
          id="loginUser"
          ref="loginUserInput"
          v-model="loginUsername"
          name="username"
          type="text"
          autocomplete="username"
          placeholder="Enter username"
          required
        >
        <label for="loginPass">Password</label>
        <input
          id="loginPass"
          v-model="loginPassword"
          name="password"
          type="password"
          autocomplete="current-password"
          placeholder="Enter password"
          required
        >
        <button class="btn primary login-submit" type="submit">Login</button>
      </form>
      <div id="loginError" class="login-error" aria-live="polite" role="status">
        {{ loginError }}
      </div>
    </div>
  </div>

  <footer
    class="footer"
    :aria-hidden="activeModal ? 'true' : undefined"
    :inert="Boolean(activeModal)"
  >
    <div class="wrap footer-row">
      <a
        class="social-chip"
        href="https://www.instagram.com/zafda.one/"
        target="_blank"
        rel="noopener noreferrer"
      >
        Instagram <strong>51K followers</strong>
      </a>
      <a
        class="social-chip"
        href="https://www.linkedin.com/company/zafda-one/"
        target="_blank"
        rel="noopener noreferrer"
      >
        LinkedIn
      </a>
      <button type="button" class="footer-action" data-open-contact @click="openModal('contact', $event)">Contact</button>
      <button type="button" class="footer-action" data-open-privacy @click="openModal('privacy', $event)">Privacy</button>
      <button type="button" class="footer-action" data-open-terms @click="openModal('terms', $event)">Terms</button>
      <button type="button" class="footer-action login-open" @click="openModal('login', $event)">Login</button>
    </div>
  </footer>

  <!-- Root-level analytics captures page views across the site. -->
  <Analytics />
</template>

<script setup>
import { Analytics } from '@vercel/analytics/vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const activeModal = ref(null)
const loginUsername = ref('')
const loginPassword = ref('')
const loginError = ref('')
const contactName = ref('')
const contactEmail = ref('')
const contactMessage = ref('')
const contactStatus = ref('')
const contactSucceeded = ref(false)

const loginDialog = ref(null)
const loginUserInput = ref(null)
const contactDialog = ref(null)
const contactNameInput = ref(null)
const contactSuccess = ref(null)
const privacyDialog = ref(null)
const privacyClose = ref(null)
const termsDialog = ref(null)
const termsClose = ref(null)

let returnFocusTarget = null
let previousBodyOverflow = ''

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const contactEmailValid = computed(() => emailPattern.test(contactEmail.value.trim().toLowerCase()))
const contactEmailInvalid = computed(() => contactEmail.value.trim() !== '' && !contactEmailValid.value)

const dialogForModal = {
  login: loginDialog,
  contact: contactDialog,
  privacy: privacyDialog,
  terms: termsDialog
}

const initialFocusForModal = {
  login: loginUserInput,
  contact: contactNameInput,
  privacy: privacyClose,
  terms: termsClose
}

function resetLoginForm () {
  loginUsername.value = ''
  loginPassword.value = ''
  loginError.value = ''
}

function resetContactForm () {
  contactName.value = ''
  contactEmail.value = ''
  contactMessage.value = ''
  contactStatus.value = ''
  contactSucceeded.value = false
}

function openModal (name, event) {
  const trigger = event?.currentTarget
  returnFocusTarget = trigger instanceof HTMLElement ? trigger : document.activeElement

  if (name === 'login') {
    resetLoginForm()
  }

  if (name === 'contact') {
    resetContactForm()
  }

  activeModal.value = name

  nextTick(() => {
    const dialog = dialogForModal[name]?.value
    if (dialog) {
      dialog.scrollTop = 0
    }
    initialFocusForModal[name]?.value?.focus()
  })
}

function closeModal () {
  if (!activeModal.value) {
    return
  }

  const modalToClose = activeModal.value
  const focusTarget = returnFocusTarget

  if (modalToClose === 'login') {
    resetLoginForm()
  }

  if (modalToClose === 'contact') {
    resetContactForm()
  }

  activeModal.value = null
  returnFocusTarget = null

  nextTick(() => {
    if (focusTarget instanceof HTMLElement && document.contains(focusTarget)) {
      focusTarget.focus()
    }
  })
}

function submitLogin () {
  loginError.value = 'Wrong password, please check and try again.'
}

function submitContact () {
  const name = contactName.value.trim()
  const email = contactEmail.value.trim()
  const message = contactMessage.value.trim()

  if (!name || !email || !message) {
    contactStatus.value = 'Please complete all fields before sending.'
    return
  }

  if (!contactEmailValid.value) {
    return
  }

  contactName.value = ''
  contactEmail.value = ''
  contactMessage.value = ''
  contactStatus.value = ''
  contactSucceeded.value = true

  nextTick(() => contactSuccess.value?.focus())
}

function getFocusableElements (dialog) {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',')

  return [...dialog.querySelectorAll(selector)].filter((element) => {
    return !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true' && element.offsetParent !== null
  })
}

function trapFocus (event) {
  const dialog = dialogForModal[activeModal.value]?.value
  if (!dialog) {
    return
  }

  const focusableElements = getFocusableElements(dialog)
  if (focusableElements.length === 0) {
    event.preventDefault()
    dialog.focus()
    return
  }

  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]
  const currentIndex = focusableElements.indexOf(document.activeElement)

  if (currentIndex === -1) {
    event.preventDefault()
    ;(event.shiftKey ? lastElement : firstElement).focus()
  } else if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault()
    lastElement.focus()
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault()
    firstElement.focus()
  }
}

function handleDocumentKeydown (event) {
  if (!activeModal.value) {
    return
  }

  if (event.key === 'Escape') {
    event.preventDefault()
    closeModal()
  } else if (event.key === 'Tab') {
    trapFocus(event)
  }
}

watch(activeModal, (currentModal, previousModal) => {
  if (currentModal && !previousModal) {
    previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  } else if (!currentModal && previousModal) {
    document.body.style.overflow = previousBodyOverflow
  }
})

onMounted(() => {
  document.addEventListener('keydown', handleDocumentKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleDocumentKeydown)
  if (activeModal.value) {
    document.body.style.overflow = previousBodyOverflow
  }
})
</script>

<style src="./styles.css"></style>
